import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";

// ---- Types ----

export type Exercise = {
  id: string;
  user_id: string | null;
  name: string;
  muscle_group: string;
  equipment: string;
  target: string;
  is_custom: number;
};

export type UsedExercise = Exercise & {
  max_weight: number | null;
  session_count: number;
  last_session_at: string;
};

export type Routine = {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  updated_at: string;
};

export type RoutineExercise = {
  id: string;
  routine_id: string;
  exercise_id: string;
  exercise_name: string;
  muscle_group: string;
  sets: number;
  min_reps: number;
  max_reps: number;
  weight_step_kg: number;
  starting_weight_kg: number;
  display_order: number;
};

export type RoutineDetail = Routine & { exercises: RoutineExercise[] };

export type Session = {
  id: string;
  user_id: string;
  routine_id?: string;
  name: string;
  started_at: string;
  completed_at?: string;
  notes?: string;
};

export type SessionExercise = {
  id: string;
  session_id: string;
  exercise_id: string;
  exercise_name: string;
  muscle_group: string;
  sets_target: number;
  min_reps: number;
  max_reps: number;
  starting_weight_kg: number;
  display_order: number;
};

export type SessionSet = {
  id: string;
  session_exercise_id: string;
  set_number: number;
  reps_done?: number;
  weight_kg?: number;
  completed_at?: string;
};

export type SessionDetail = Session & {
  exercises: SessionExercise[];
  sets: SessionSet[];
};

export type Suggestion = {
  exercise_id: string;
  exercise_name: string;
  current_weight: number;
  suggest_weight: number;
  weight_step: number;
  routine_exercise_id: string | null;
  routine_id: string | null;
};

// ---- Exercises ----

export function useExercises() {
  return useQuery({
    queryKey: ["exercises"],
    queryFn: () => api.get<Exercise[]>("/exercises"),
  });
}

export function useUsedExercises() {
  return useQuery({
    queryKey: ["exercises", "used"],
    queryFn: () => api.get<UsedExercise[]>("/exercises/used"),
  });
}

export function useCreateExercise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; muscle_group: string }) =>
      api.post<Exercise>("/exercises", data),
    onSuccess: (ex) => {
      qc.setQueryData<Exercise[]>(["exercises"], (prev) => [...(prev ?? []), ex]);
    },
  });
}

// ---- Routines ----

export function useRoutines() {
  return useQuery({
    queryKey: ["routines"],
    queryFn: () => api.get<Routine[]>("/routines"),
  });
}

export function useRoutine(id: string) {
  return useQuery({
    queryKey: ["routines", id],
    queryFn: () => api.get<RoutineDetail>(`/routines/${id}`),
    enabled: !!id,
  });
}

export function applyRoutinePatch<T extends Routine | RoutineDetail>(routine: T, patch: { name?: string; description?: string }) {
  return {
    ...routine,
    name: patch.name ?? routine.name,
    description: patch.description ?? routine.description,
    updated_at: new Date().toISOString(),
  } as T;
}

export function updateRoutineCache(qc: ReturnType<typeof useQueryClient>, id: string, patch: { name?: string; description?: string }) {
  qc.setQueryData<Routine[]>(["routines"], (prev) =>
    prev?.map((routine) => (routine.id === id ? applyRoutinePatch(routine, patch) : routine)) ?? prev
  );
  qc.setQueryData<RoutineDetail | undefined>(["routines", id], (prev) =>
    prev ? applyRoutinePatch(prev, patch) : prev
  );
}

export function useCreateRoutine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name: string; description?: string }) =>
      api.post<Routine>("/routines", data),
    onSuccess: (routine) => {
      qc.setQueryData<Routine[]>(["routines"], (prev) => [routine, ...(prev ?? [])]);
      qc.setQueryData<RoutineDetail>(["routines", routine.id], { ...routine, exercises: [] });
      qc.invalidateQueries({ queryKey: ["routines"] });
    },
  });
}

export function useUpdateRoutine(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { name?: string; description?: string }) =>
      api.put(`/routines/${id}`, data),
    onSuccess: (_, variables) => {
      updateRoutineCache(qc, id, variables);
      qc.invalidateQueries({ queryKey: ["routines"] });
      qc.invalidateQueries({ queryKey: ["routines", id] });
    },
  });
}

export function useDeleteRoutine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/routines/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["routines"] }),
  });
}

export function useAddRoutineExercise(routineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Omit<RoutineExercise, "id" | "routine_id" | "exercise_name" | "muscle_group">) =>
      api.post(`/routines/${routineId}/exercises`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["routines", routineId] }),
  });
}

export function useUpdateRoutineExercise() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ routineId, exId, data }: { routineId: string; exId: string; data: Partial<RoutineExercise> }) =>
      api.put(`/routines/${routineId}/exercises/${exId}`, data),
    onSuccess: (_, { routineId }) => qc.invalidateQueries({ queryKey: ["routines", routineId] }),
  });
}

export function useRemoveRoutineExercise(routineId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (exId: string) => api.delete(`/routines/${routineId}/exercises/${exId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["routines", routineId] }),
  });
}

// ---- Sessions ----

export function useSessions() {
  return useQuery({
    queryKey: ["sessions"],
    queryFn: () => api.get<Session[]>("/sessions"),
  });
}

export function useSession(id: string) {
  return useQuery({
    queryKey: ["sessions", id],
    queryFn: () => api.get<SessionDetail>(`/sessions/${id}`),
    enabled: !!id,
  });
}

export function useStartSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { routine_id?: string; name: string }) =>
      api.post<{ id: string }>("/sessions", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions"] }),
  });
}

export function useCompleteSession(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notes?: string) =>
      api.patch(`/sessions/${id}`, {
        completed_at: new Date().toISOString(),
        notes,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions", id] }),
  });
}

export function useLogSet(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      session_exercise_id: string;
      set_number: number;
      reps_done: number;
      weight_kg: number;
    }) => api.post<{ id: string }>(`/sessions/${sessionId}/sets`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions", sessionId] }),
  });
}

export function useUpdateSet(sessionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ setId, data }: { setId: string; data: { reps_done?: number; weight_kg?: number } }) =>
      api.put(`/sessions/${sessionId}/sets/${setId}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions", sessionId] }),
  });
}

export function useSuggestions(sessionId: string) {
  return useQuery({
    queryKey: ["suggestions", sessionId],
    queryFn: () => api.get<Suggestion[]>(`/sessions/${sessionId}/suggestions`),
    enabled: !!sessionId,
  });
}

export function useExerciseProgress(exerciseId: string) {
  return useQuery({
    queryKey: ["progress", exerciseId],
    queryFn: () =>
      api.get<Array<{ started_at: string; max_weight: number; volume: number }>>(
        `/sessions/progress/${exerciseId}`
      ),
    enabled: !!exerciseId,
  });
}
